import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'components/Snackbar';
import { Button } from '@repo/ui';
import { Footer } from 'components/Footer';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { PageContainer, PageHeader, SectionLabel } from 'components/UI';
import { PROSE_MAX_WIDTH } from 'constants/layout';
import { PhoneVerificationDrawer } from 'components/UI/PhoneVerificationDrawer';
import { EditEmailDrawer } from 'components/UI/EditEmailDrawer';
import { ProfileAvatar } from 'components/pages/profile/ProfileAvatar';
import { GeneralInfo } from 'components/pages/profile/GeneralInfo';
import { LoginInfo } from 'components/pages/profile/LoginInfo';
import { SecurityInfo } from 'components/pages/profile/SecurityInfo';
import { TwoFactorDrawer } from 'components/pages/profile/TwoFactorDrawer';
import { ConnectedAccounts } from 'components/pages/profile/ConnectedAccounts';
import { EditFieldModal } from 'components/pages/profile/EditFieldModal';
import { ProfileSkeleton } from 'components/pages/profile/ProfileSkeleton';
import { LogOut } from 'components/pages/profile/LogOut';
import { useAvatar, useProfileActions } from 'hooks';
import { FormValues, EditField } from './types';
import { AVATAR_ALIGN } from './layout';

import { useDispatch, useSelector } from 'store/hooks';

import { useForm } from 'react-hook-form';

import { Stack, Box, useMediaQuery, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BellIcon, CircleHelpIcon, LogOutIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import { pushState } from 'hooks/usePushPage';
import { FieldRow } from 'components/Fields/FieldRow';
import { helpNavState } from 'pages/Help/navState';
import { useAuthFactorsQuery } from 'hooks/api/useAuthFactors';

export const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();

  const methods = useForm<FormValues>({
    defaultValues: {
      first_name: '',
      last_name: '',
      username: '',
      birthday: null,
      country: '',
      phone: '',
      gender: '',
      email: '',
      current_password: '',
      new_password: '',
    },
    mode: 'onSubmit',
  });

  const { setValue, setError } = methods;

  const { profile, isAccountLinked } = useSelector(({ auth }) => auth);
  const countriesState = useSelector(state => state.countries.countries);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [initialValues, setInitialValues] = useState<Partial<FormValues>>({
    first_name: '',
    last_name: '',
    username: '',
    birthday: null,
    country: '',
    phone: '',
  });
  const [editField, setEditField] = useState<EditField | null>(null);
  const [originalValues, setOriginalValues] = useState<Partial<FormValues>>({});
  const [passwordCurrent, setPasswordCurrent] = useState<string>('');
  const [passwordNew, setPasswordNew] = useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [showPhoneError, setShowPhoneError] = useState<boolean>(false);

  // Custom hooks
  const avatar = useAvatar();
  const profileActions = useProfileActions({
    methods,
    editField,
    setEditField,
    setError,
    setShowPhoneError,
    setPhoneError,
    openSnackbar,
    profile,
    originalValues,
  });

  useEffect(() => {
    if (profile !== null) {
      setValue('first_name', profile.first_name ?? '');
      setValue('last_name', profile.last_name ?? '');
      setValue('username', profile.username);
      setValue('birthday', profile.birthday ? new Date(profile.birthday) : null);
      setValue('country', profile.country);
      setValue('phone', profile.phone ?? '');
      setValue('gender', profile.gender ?? '');
      // VERA-291 — was never seeded from `profile` (only ever written by the
      // old, backend-no-op EditEmailDrawer flow), so LoginInfo's email row
      // rendered blank until the user opened the (fake) edit flow once. The
      // real verified flow refreshes `profile.email` via getProfile() on
      // confirm, so this needs to actually flow through.
      setValue('email', profile.email ?? '');
      setInitialValues({
        first_name: profile.first_name ?? '',
        last_name: profile.last_name ?? '',
        username: profile.username ?? '',
        birthday: profile.birthday ? new Date(profile.birthday) : null,
        country: profile.country ?? '',
        phone: profile.phone ?? '',
      });
      if (profile.avatar) {
        const next = avatar.resolveAvatarUrl(profile.avatar);
        const img = new Image();
        img.onload = () => {
          avatar.setAvatarImage(next);
        };
        img.onerror = () => {};
        img.src = next;
      }
    }
  }, [profile]);

  useEffect(() => {
    if (countriesState === null) {
      import('store/countries/actions').then(m => dispatch(m.getCountries(() => {})));
    }
  }, [countriesState]);

  const getAvatar = () => avatar.getAvatar(profile);

  const [isLogOutOpen, setIsLogOutOpen] = useState<boolean>(false);

  // VERA-291 — email and phone now edit via the shared drawers (components/UI
  // EditEmailDrawer / PhoneVerificationDrawer) rather than EditFieldModal, so
  // Profile gets the same verified flow as the Notifications page and a
  // single implementation per field (working-conventions §36). EditEmailDrawer
  // now confirms the change inline (OTP code, not an emailed link), so there
  // is no separate pending/"undo" state to track here anymore — the drawer's
  // own success step covers that, and `getProfile()` (dispatched inside the
  // drawer on confirm) keeps `profile.email` — and therefore the form value
  // seeded above — current.
  const [emailDrawerOpen, setEmailDrawerOpen] = useState<boolean>(false);
  const [phoneDrawerOpen, setPhoneDrawerOpen] = useState<boolean>(false);

  // VRG-490 (D8) — a Security SECTION on this page (not a new route), opening
  // one step-machine drawer for enrolled factors + enroll/remove/recovery
  // codes. `factors`/`isLoading` are fetched here (not inside the drawer)
  // so the SecurityInfo row's summary ("On · Authenticator app") is correct
  // even before the drawer has ever been opened.
  const [twoFactorDrawerOpen, setTwoFactorDrawerOpen] = useState<boolean>(false);
  const { factors: authFactors, isLoading: isLoadingAuthFactors } = useAuthFactorsQuery();

  return (
    <>
      {!profile && <ProfileSkeleton />}
      {profile && (
        <Stack
          sx={{
            background: theme.palette.background.paper,
            // Fill the viewport — see Notifications/index.tsx.
            minHeight: '100vh',
            // NO `px` — the side gutter belongs to `PageContainer` below, which
            // owns it for every page. Left here it was one of four different page
            // gutters in the app.
            // NO `pt` — the top gap belongs to PageHeader, which owns every other
            // gap in the header block. Left here it was 32px above the back-link
            // against 12px below it, and each page was free to pick a different
            // number.
            pb: { xs: 4, md: 8 },
          }}
        >
          {/* Shared column outside, READING measure inside. The nesting is a
              visual NO-OP — both boxes centre on the same axis, so a 640 column
              lands in exactly the same place it did under the page's old 32px
              gutter, at every viewport. What it buys is that the gutter and the
              outer cap are no longer this page's to choose, and that dropping the
              reading measure later lands the page on the shared column for free.
              See `PROSE_MAX_WIDTH` for why these pages keep 640. */}
          <PageContainer>
          <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
            {/*
              NO back-link, deliberately — and this is the one page where its
              absence is a fix rather than a gap.

              It used to render "← My certificates". That made sense when Profile
              was reached from a hamburger and had no other way out. It is now a
              PRIMARY DESTINATION: `PRIMARY_NAV_ITEMS` lists it beside
              Certificates and Activity, `showsPrimaryNav()` returns true for
              `ROUTES.PROFILE`, and both presentations of that nav render here —
              the bottom bar on a phone, the left rail on a desktop. So
              Certificates is one tap away from this page at every width, and the
              back-link was a SECOND control pointing at a destination already on
              screen, two rows below it.

              Worse than redundant, it misdescribed the structure: a back-link
              says "you came from there and this is beneath it", which is the
              relationship Notifications has to Profile (a pushed settings page,
              and it keeps its back-link). Profile is not beneath Certificates —
              they are siblings, and the nav says so correctly.

              The removal is scoped to THIS page. `PageHeader` keeps `backLabel` /
              `backTo` / `onBack`, which pages/MyCode, pages/Notifications and the
              certificate sub-views all still use — those are genuinely pushed
              pages whose parent is not in the primary nav.
            */}
            <PageHeader title='Profile' />
            {/*
              LEFT-ALIGNED, on the page's single axis — `AVATAR_ALIGN` from
              pages/Profile/layout.ts, which ProfileSkeleton reads too so the
              loading and loaded states cannot disagree.

              Centring this block has now been proposed twice (VERA-291, and again
              on 2026-08-23 where it was built, viewed at both breakpoints and
              rejected). The full reasoning — including the one arrangement that
              WOULD justify centring, a committed centred header band rather than a
              lone centred photo — is written up on the constant. Read that before
              re-opening it; it is the argument, not just the outcome.
            */}
            {/*
              48 below the avatar, against 32 between sections — deliberately the
              larger of the two.

              The avatar is not a section: it is the page's subject, and GENERAL is
              the first group ABOUT it. At the same 32 the photo read as though it
              belonged to the General group rather than heading the page, and the
              first boundary on the page was the least clear one.
            */}
            <Stack mb={6} alignItems={AVATAR_ALIGN}>
              <ProfileAvatar
                avatarImage={avatar.avatarImage}
                onFileSelect={avatar.handleFileSelect}
                onAvatarRemoved={avatar.resetAvatar}
                firstName={profile.first_name}
                lastName={profile.last_name}
              />
            </Stack>

            <GeneralInfo
              methods={methods}
              countriesState={countriesState ?? []}
              onEditField={field => {
                if (field.key === 'phone') {
                  setPhoneDrawerOpen(true);
                  return;
                }
                if (field.key === 'name') {
                  setOriginalValues({
                    first_name: methods.getValues('first_name'),
                    last_name: methods.getValues('last_name'),
                  });
                } else {
                  setOriginalValues({
                    [field.key]: methods.getValues(field.key as keyof FormValues),
                  });
                }
                setEditField(field);
              }}
            />

            {/*
              VERA-304 — the `MY CODE` section that used to sit here is gone, and
              nothing replaced it in the flow.

              It was placed between General and Log in on the argument that the
              code is about "who you are to other people", the same thing General
              defines. That argument survives; what did not is putting a 240px QR
              card in a settings list. The section had grown a second job the
              list could not hold (reading someone else's code needs a camera,
              not a row), and the QR was on screen on every visit to a page most
              visits come to for something else.

              It is now a pushed page, entered from the QR button beside the
              avatar — one glyph in the header instead of a card in the middle.
              See pages/MyCode.
            */}
            <LoginInfo
              methods={methods}
              onEditField={field => {
                if (field.key === 'email') {
                  setEmailDrawerOpen(true);
                  return;
                }
                if (field.key === 'password') {
                  setOriginalValues({
                    current_password: passwordCurrent,
                    new_password: passwordNew,
                  });
                } else {
                  setOriginalValues({
                    [field.key]: methods.getValues(field.key as keyof FormValues),
                  });
                }
                setEditField(field);
              }}
            />

            {/*
              VRG-490 (D8) — Security, right after Log in: the same "Profile
              is who you are, Security is how you prove it's you" adjacency.
              Password stays in LoginInfo above (app-side scope here is
              two-factor only — D7's password move was platform-only); this
              section is the entire two-factor surface.
            */}
            <SecurityInfo
              factors={authFactors}
              isLoading={isLoadingAuthFactors}
              onOpen={() => setTwoFactorDrawerOpen(true)}
            />

            {/*
              VERA-315 — notification preferences moved OFF the Activity tab and
              under Profile, where the rest of the account settings live. The
              Activity tab now holds the cross-certificate feed its name implies.

              A PUSHED sub-page rather than another section inlined here. Profile
              already runs long (avatar, seven general fields, login info,
              connected accounts, log out, footer), and the notifications surface
              is not a field or two — it is two contact rows plus a four-column
              switch grid with its own always-sent caveat. Inlining it would bury
              the settings above it and give one page two unrelated jobs; a row
              costs 56px and keeps both pages scannable.

              It DOES take a `SectionLabel`, reversing an earlier call here that
              cited the single-child-group rule (working-conventions §20). That
              rule is about a task hierarchy, where a group node with one child
              adds a level of nesting and no information. A section label on a
              page is doing a different job: every other group on this page is
              headed (General, Log in, Connected accounts), so the lone unheaded
              row between two headed ones did not read as "deliberately ungrouped",
              it read as unfinished — which is exactly how it was reported.

              "Preferences", not "Notifications": a heading that restates the one
              row beneath it is the redundancy the single-child argument was
              really objecting to. Naming the CATEGORY says something the row does
              not, and gives the next preference row somewhere to go.
            */}
            <Stack mb={4}>
              <SectionLabel>Preferences</SectionLabel>
              <FieldRow
                label='Notifications'
                icon={<BellIcon size={20} strokeWidth={1.5} />}
                /*
                  `placeholder=''` is load-bearing. FieldRow defaults it to
                  "Add", which is right for an empty FIELD ("no date of birth
                  yet, tap to add one") and wrong here: this row has no value to
                  add, it goes somewhere. Left at the default it rendered
                  "Notifications ......... Add ›", which reads as a prompt to
                  create a notification.
                */
                placeholder=''
                // `pushState()` is the SENDER half of the push rule: the link declares
                // that this is a deeper navigation, and the destination reads it. It is
                // not inferred on arrival — see usePushPage for why.
                onClick={() => navigate(ROUTES.NOTIFICATIONS, { state: pushState() })}
              />
            </Stack>

            <ConnectedAccounts isAccountLinked={isAccountLinked} />

            {/*
              VRG-418 — the Help surface, reached from the account page like the rest of
              the things you go and read rather than change.

              "SUPPORT" names the CATEGORY, which is the same call the Preferences group
              above records: a heading that restates the single row beneath it ("Help" over
              "Help") is the redundancy the single-child-group rule (§20) is really
              objecting to, and every other group on this page is headed, so leaving this
              row bare would read as unfinished rather than as deliberately ungrouped.

              The heading is NOT a promise of a support channel. There is deliberately no
              contact affordance anywhere in this feature — no mailto, no "still need
              help?" block — and this section is where one would go if that ever changes.
            */}
            <Stack mb={4}>
              <SectionLabel>Support</SectionLabel>
              <FieldRow
                label='Help'
                icon={<CircleHelpIcon size={20} strokeWidth={1.5} />}
                // `placeholder=''` for the same reason as the Notifications row above:
                // FieldRow's default is "Add", which reads as a prompt to create
                // something on a row that simply goes somewhere.
                placeholder=''
                // No `pushState()` here, unlike the Notifications row. Help declares
                // itself `pushOnly` (it has no cold-entry path at all), so it animates on
                // the strength of the ROUTE rather than a sender flag; the state it does
                // need is where to send the reader back to.
                onClick={() =>
                  navigate(ROUTES.HELP, { state: helpNavState('Profile', ROUTES.PROFILE) })
                }
              />
            </Stack>

            {/*
              VERA-291 footer-consistency follow-up: this trigger is
              persistent chrome (always rendered on the page, not opened by a
              prior click), so it takes `destructive="hover"` per
              working-conventions §32 — neutral at rest, red on hover/focus.
              The always-red `destructive` treatment is reserved for the
              transient confirm drawer this opens (see LogOut.tsx).
            */}
            {/*
              LEFT-ALIGNED, not centred. MUI centres a Button's content by
              default, so `fullWidth` put the icon and label in the middle of a
              full-width control — a second axis on a page where the title, every
              field label, every row and the avatar all resolve to one left edge.

              This is the same argument that settled the avatar: alignment is a
              property of the PAGE, and one stray centred element reads as a
              mistake rather than as emphasis. The button stays full-width (it is
              the page's last action and the width is what marks it as such); only
              its CONTENT moves to the left edge.
            */}
            <Button
              variant='secondary'
              destructive='hover'
              fullWidth
              onClick={() => setIsLogOutOpen(true)}
              startIcon={<LogOutIcon size={20} strokeWidth={1.5} />}
              /*
                `pl` as well as `justifyContent`. flex-start alone left-aligns the
                content INSIDE the button, but the Button's own 32px padding then
                held the glyph at x=45 while every row, caption and the title on
                this page sit at x=16 — visibly a second axis, which is the thing
                being fixed. 16 lands it at ~29.

                Not 0: a bordered control with its content flush against the border
                reads as a rendering fault. 29 vs 16 is the residual cost of the
                button HAVING a border at all, and it is small enough to read as
                one axis; the 45 it replaced was not.
              */
              /*
                `minHeight: 44` to match the Connected-accounts rows it sits
                directly beneath. Both carry 10px of vertical padding, so the 4px
                difference came from CONTENT: those rows wrap their glyph in a
                24px box, this button's icon is 20px. Padding alone could never
                have reconciled them, which is why they drifted.

                44 is also the tap-target floor, so the shorter one was the wrong
                one to keep.
              */
              sx={{ justifyContent: 'flex-start', pl: 2, minHeight: 44 }}
            >
              Log out
            </Button>

            <Box sx={{ mt: 4, '& > *': { pt: '12px !important', pb: '12px !important' } }}>
              <Footer />
            </Box>

            {isLogOutOpen && (
              <DrawerPopup
                title='Log out'
                titleUppercase={false}
                fullScreen={false}
                titleSx={{ color: theme.palette.text.primary, fontSize: '20px', fontWeight: 'normal' }}
                backgroundColor={theme.palette.background.paper}
                child={<LogOut handleClose={() => setIsLogOutOpen(false)} />}
                handleOpen={isLogOutOpen}
                handleClose={() => setIsLogOutOpen(false)}
              />
            )}

            <EditFieldModal
              editField={editField}
              methods={methods}
              originalValues={originalValues}
              countriesState={countriesState ?? []}
              countrySearch={countrySearch}
              setCountrySearch={setCountrySearch}
              passwordCurrent={passwordCurrent}
              setPasswordCurrent={setPasswordCurrent}
              passwordNew={passwordNew}
              setPasswordNew={setPasswordNew}
              showCurrentPassword={showCurrentPassword}
              setShowCurrentPassword={setShowCurrentPassword}
              showNewPassword={showNewPassword}
              setShowNewPassword={setShowNewPassword}
              onClose={() => setEditField(null)}
              onSubmit={profileActions.onSubmit}
              onCancel={profileActions.handleCancel}
              onRemoveField={profileActions.onRemoveField}
            />

            <EditEmailDrawer
              open={emailDrawerOpen}
              initialEmail={profile?.email}
              onClose={() => setEmailDrawerOpen(false)}
            />

            <PhoneVerificationDrawer
              open={phoneDrawerOpen}
              onClose={() => setPhoneDrawerOpen(false)}
              initialPhone={profile?.phone}
              surface='profile'
            />

            <TwoFactorDrawer
              open={twoFactorDrawerOpen}
              onClose={() => setTwoFactorDrawerOpen(false)}
            />
          </Box>
          </PageContainer>
        </Stack>
      )}
    </>
  );
};

export default ProfilePage;
